import type { AttributableHaloResult, BaselineSeriesPoint } from '@halo/web-types';
import { GATE_REASON_COPY } from '@halo/web-types';

const cardCls =
  'rounded-lg border border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900 p-4';

const usd = (v: number) => `$${Math.round(v).toLocaleString()}`;

interface BaselineComparison {
  label: string;
  baselineTotal: number;
  actualTotal: number;
  days: number;
  totalDays: number;
}

function comparison(
  label: string,
  points: BaselineSeriesPoint[],
  value: (p: BaselineSeriesPoint) => number | null,
): BaselineComparison {
  const covered = points.filter((p) => value(p) !== null);
  return {
    label,
    baselineTotal: covered.reduce((s, p) => s + (value(p) ?? 0), 0),
    actualTotal: covered.reduce((s, p) => s + p.actualRevenue, 0),
    days: covered.length,
    totalDays: points.length,
  };
}

export function RangeSummary({
  points,
  summary,
}: {
  points: BaselineSeriesPoint[];
  summary: AttributableHaloResult | undefined;
}) {
  if (points.length === 0) return null;
  const actualTotal = points.reduce((s, p) => s + p.actualRevenue, 0);
  const comparisons = [
    comparison('vs raw baseline', points, (p) => p.rawBaseline),
    comparison('vs media-adjusted', points, (p) => p.mediaAdjustedBaseline),
    comparison('vs selected baseline', points, (p) => p.selectedBaseline),
  ];

  return (
    <div className={cardCls}>
      <TierStrip summary={summary} />
      <div className="flex flex-wrap items-stretch gap-x-8 gap-y-3">
        <div>
          <p className="text-[11px] uppercase tracking-wide text-gray-400">Actual revenue</p>
          <p className="text-lg font-semibold tabular-nums text-gray-900 dark:text-gray-100">
            {usd(actualTotal)}
          </p>
          <p className="text-[11px] text-gray-400">{points.length} days</p>
        </div>
        {comparisons.map((c) => {
          if (c.days === 0) {
            return (
              <div key={c.label}>
                <p className="text-[11px] uppercase tracking-wide text-gray-400">{c.label}</p>
                <p className="text-lg font-semibold text-gray-400">n/a</p>
                <p className="text-[11px] text-gray-400">no data in range</p>
              </div>
            );
          }
          const diff = c.actualTotal - c.baselineTotal;
          const pct = c.baselineTotal !== 0 ? (diff / c.baselineTotal) * 100 : null;
          const positive = diff >= 0;
          return (
            <div key={c.label}>
              <p className="text-[11px] uppercase tracking-wide text-gray-400">{c.label}</p>
              <p
                className={`text-lg font-semibold tabular-nums ${positive ? 'text-green-600' : 'text-red-600'}`}
              >
                {positive ? '+' : '-'}
                {usd(Math.abs(diff))}
                {pct !== null && (
                  <span className="ml-1 text-xs font-medium">
                    ({positive ? '+' : '-'}
                    {Math.abs(pct).toFixed(1)}%)
                  </span>
                )}
              </p>
              <p className="text-[11px] text-gray-400">
                baseline {usd(c.baselineTotal)}
                {c.days < c.totalDays ? ` over ${c.days}/${c.totalDays} days` : ''}
              </p>
            </div>
          );
        })}
      </div>
      <p className="mt-2 text-[10px] text-gray-400">
        Differences are actual minus baseline summed over the selected range; media-adjusted covers
        only days where it was computed. Attributable halo is an estimate: excess above the
        quiet-day noise floor on elevated-media days only.
      </p>
    </div>
  );
}

function TierStrip({ summary }: { summary: AttributableHaloResult | undefined }) {
  if (!summary) return null;
  return (
    <div className="flex flex-wrap items-stretch gap-x-8 gap-y-3 border-b border-gray-100 dark:border-gray-800 pb-3 mb-3">
      <div>
        <p className="text-[11px] uppercase tracking-wide text-gray-400">
          Est. media-attributable halo
        </p>
        {summary.attributableHalo !== null ? (
          <>
            <p className="text-lg font-semibold tabular-nums text-green-600">
              ${Math.round(summary.attributableHalo).toLocaleString()}
            </p>
            <p className="text-[11px] text-gray-400">
              noise floor {summary.noiseFloorPct !== null && summary.noiseFloorPct >= 0 ? '+' : ''}
              {summary.noiseFloorPct?.toFixed(1)}% over {summary.quietDays} quiet days &middot;{' '}
              {summary.elevatedDays} elevated days credited
            </p>
          </>
        ) : (
          <>
            <p className="text-lg font-semibold text-gray-400">n/a</p>
            <p className="text-[11px] text-gray-400">
              {summary.gateReason ? GATE_REASON_COPY[summary.gateReason] : 'unavailable'}
            </p>
          </>
        )}
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wide text-gray-400">Net halo</p>
        <p
          className={`text-lg font-semibold tabular-nums ${summary.netHalo >= 0 ? 'text-gray-800 dark:text-gray-100' : 'text-red-600'}`}
        >
          ${Math.round(summary.netHalo).toLocaleString()}
        </p>
      </div>
      <div>
        <p className="text-[11px] uppercase tracking-wide text-gray-400">Above baseline (gross)</p>
        <p className="text-lg font-semibold tabular-nums text-gray-500 dark:text-gray-400">
          ${Math.round(summary.grossPositiveHalo).toLocaleString()}
        </p>
        <p className="text-[11px] text-gray-400">includes model noise; diagnostic only</p>
      </div>
    </div>
  );
}
